import React, { useState, useEffect } from "react";
import { Col, Row, Card, Button, Input, notification, message, Tooltip, Table, Skeleton, Popconfirm} from "antd";
import { SettingFilled, DeleteFilled, PlusOutlined, SaveFilled, FolderOpenFilled } from "@ant-design/icons";
import { 
  handleAddNewType, 
  handleTypeChange, 
  handleDeleteType, 
  handleSaveTypes 
} from "./components/DependencySettings/dependencyTypeLogic";
import { 
  handleAddNewAttribute, 
  handleAttributeChange, 
  handleDeleteAttribute, 
  handleSaveAttributes 
} from "./components/DependencySettings/dependencyAttributeLogic";
import WithPermission from "../Common/WithPermission/WithPermission";
import PageTitle from "../Common/PageTitle/PageTitle";
import ChangeLog from "../Common/ChangeLog/ChangeLog";
import { getDependencyTypes, getDependencyTypeById, getDependencyAttributes, getDependencyAttributesById } from "../../API/dependencies";
import Areas from "./components/Areas/Areas";

import { useAuth } from "../Context/AuthContext";

import "./Settings.css";

const Settings = () => {
  // Permisos
  const { userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  const [api, contextHolder] = notification.useNotification();
  // Estados para Tipos y Atributos de Dependencia
  const [dependencyTypes, setDependencyTypes] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [editedTypes, setEditedTypes] = useState([]);
  const [editedAttributes, setEditedAttributes] = useState([]);
  const [deletedTypes, setDeletedTypes] = useState([]);
  const [deletedAttributes, setDeletedAttributes] = useState([]);
  // Estado de carga
  const [isLoading, setIsLoading] = useState(true);


  // Estados para ChangeLog
  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  // Función para cargar los tipos de dependencia
  const fetchDependencyTypes = async () => {
    try {
      const data = await getDependencyTypes();
      setDependencyTypes(data || []);
      setEditedTypes(data || []);
    } catch (error) {
      message.error(error.message || 'Error al cargar los tipos de dependencia.');
      setDependencyTypes([]);
      setEditedTypes([]);
    }
  };

  // Función para cargar los atributos
  const fetchAttributes = async () => {
    try {
      const data = await getDependencyAttributes();
      setAttributes(data || []);
      setEditedAttributes(data || []);
    } catch (error) {
      message.error(error.message || 'Error al cargar los atributos.');
      setAttributes([]);
      setEditedAttributes([]);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    fetchDependencyTypes();
    fetchAttributes();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchDependencyTypes(), fetchAttributes()]);
      setIsLoading(false);
    };
    loadData();
  }, []);

  // Función para manejar el guardado de tipos
  const handleSaveTypesWrapper = async () => {
    await handleSaveTypes(
      editedTypes,
      deletedTypes,
      fetchDependencyTypes,
      api
    );
  };

  // Función para manejar el guardado de atributos
  const handleSaveAttributesWrapper = async () => {
    await handleSaveAttributes(
      editedAttributes,
      deletedAttributes,
      fetchAttributes,
      api
    );
  };

  // Función para abrir el ChangeLog de Tipos de Dependencia
  const handleOpenTypeChangeLog = async (type) => {
    if (!type.id || type.isNew) {
      api.warning({ message: "No hay historial", description: "Los elementos nuevos no tienen historial aún." });
      return;
    }
    try {
      const response = await getDependencyTypeById(type.id);
      setChangeLogData(type);
      setChangeLogLogs(response.logs || []);
      setChangeLogVisible(true);
    } catch (error) {
      api.error({ message: "Error al obtener logs", description: error.message });
    }
  };

  // Función para abrir el ChangeLog de Atributos
  const handleOpenAttributeChangeLog = async (attribute) => {
    if (!attribute.id || attribute.isNew) {
      api.warning({ message: "No hay historial", description: "Los elementos nuevos no tienen historial aún." });
      return;
    }
    try {
      const response = await getDependencyAttributesById(attribute.id);
      setChangeLogData(attribute);
      setChangeLogLogs(response.logs || []);
      setChangeLogVisible(true);
    } catch (error) {
      api.error({ message: "Error al obtener logs", description: error.message });
    }
  };

  // Función para cerrar el ChangeLog
  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      requiredPermission="read"
      redirectTo="/home"
    >
      <>
        {contextHolder}
        
        <Row justify="start">
          <Col>
            <PageTitle title="Configuraciones" icon="settings" />
          </Col>
        </Row>

        <Row style={{ marginTop: 24 }}>
          <Col span={24}>
            <Areas />
          </Col>
        </Row>

        {isLoading ? (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </Col>
              <Col xs={24} md={12}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </Col>
            </Row>
          </>
          ) : (
            <Row gutter={24} style={{ marginTop: 16 }}>
              {/* Card para Tipos de Dependencia */}
              <Col span={12}>
                <Card 
                  className="dependency-card"
                  title={
                    <span>
                      <SettingFilled style={{ marginRight: 8 }} />
                      Tipos de Dependencia
                    </span>
                  }
                  extra={
                    <Tooltip title="Agregar Tipo">
                      <Button 
                        className="add-type-button" 
                        icon={<PlusOutlined />} 
                        onClick={() => handleAddNewType(editedTypes, setEditedTypes)} 
                        type="primary"
                      />
                    </Tooltip>
                  }
                >
                  <div className="scrollable-card-content">
                    {editedTypes.map((type, index) => (
                      <Row key={index} style={{ marginBottom: 8 }} gutter={[8, 0]}>
                        <Col span={20}>
                          <Input
                            className={
                              type.isNew
                                ? "new-input-highlight"
                                : type.isUpdated
                                ? "edited-input-highlight"
                                : ""
                            }
                            style={{ width: '100%' }}
                            value={type.name}
                            onChange={(e) =>
                              handleTypeChange(index, e.target.value, editedTypes, setEditedTypes)
                            }
                            maxLength={50}
                          />
                          {type.name && type.name.length >= 50 && (
                            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                              Has alcanzado el límite de 50 caracteres
                            </div>
                          )}
                        </Col>
                        <Col span={2}>
                          <Tooltip title="Ver Log">
                            <Button 
                              className="changelog-button"
                              icon={<FolderOpenFilled />} 
                              onClick={() => handleOpenTypeChangeLog(type)}
                              disabled={type.isNew || !type.id}
                            />
                          </Tooltip>
                        </Col>
                        <Col span={2}>
                          <Tooltip title="Eliminar">
                            <Button 
                              className="delete-button"
                              icon={<DeleteFilled />} 
                              onClick={() => handleDeleteType(
                                index, 
                                editedTypes, 
                                setEditedTypes, 
                                deletedTypes, 
                                setDeletedTypes, 
                                api
                              )} 
                            />
                          </Tooltip>
                        </Col>
                      </Row>
                    ))}
                  </div>
                  <div className="button-group" style={{ marginTop: 16 }}>
                    <Button 
                      className="save-type-button"
                      icon={<SaveFilled />}
                      onClick={handleSaveTypesWrapper}
                      loading={false}
                    >
                      Guardar
                    </Button>
                  </div>
                </Card>
              </Col>

              {/* Card para Atributos de Dependencia */}
              <Col span={12}>
                <Card 
                  className="dependency-card"
                  title={
                    <span>
                      <SettingFilled style={{ marginRight: 8 }} />
                      Atributos de Dependencia
                    </span>
                  }
                  extra={
                    <Tooltip title="Agregar Atributo">
                      <Button 
                        className="add-type-button" 
                        icon={<PlusOutlined />} 
                        onClick={() => handleAddNewAttribute(editedAttributes, setEditedAttributes)}
                      />
                    </Tooltip>
                  }
                >
                  <div className="scrollable-card-content">
                    {editedAttributes.map((attribute, index) => (
                      <Row key={index} style={{ marginBottom: 8 }} gutter={[8, 0]}>
                        <Col span={20}>
                          <Input
                            className={
                              attribute.isNew
                                ? "new-input-highlight"
                                : attribute.isUpdated
                                ? "edited-input-highlight"
                                : ""
                            }
                            value={attribute.key} 
                            onChange={(e) => handleAttributeChange(
                              index, 
                              e.target.value, 
                              editedAttributes, 
                              setEditedAttributes
                            )}
                            maxLength={50}
                          />
                          {attribute.key && attribute.key.length >= 50 && (
                            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                              Has alcanzado el límite de 50 caracteres
                            </div>
                          )}
                        </Col>
                        <Col span={2}>
                          <Tooltip title="Ver Log">
                            <Button
                              className="changelog-button"
                              icon={<FolderOpenFilled />} 
                              onClick={() => handleOpenAttributeChangeLog(attribute)}
                              disabled={attribute.isNew || !attribute.id}
                            />
                          </Tooltip>
                        </Col>
                        <Col span={2}>
                          <Tooltip title="Eliminar">
                            <Button
                              className="delete-button"
                              icon={<DeleteFilled />} 
                              onClick={() => handleDeleteAttribute(
                                index, 
                                editedAttributes, 
                                setEditedAttributes, 
                                deletedAttributes, 
                                setDeletedAttributes, 
                                api
                              )} 
                              style={{ width: '100%' }}
                            />
                          </Tooltip>
                        </Col>
                      </Row>
                    ))}
                  </div>
                  <div className="button-group" style={{ marginTop: 16 }}>
                    <Button 
                      className="save-type-button"
                      icon={<SaveFilled />}
                      onClick={handleSaveAttributesWrapper}
                      loading={false}
                    >
                      Guardar
                    </Button>
                  </div>
                </Card>
              </Col>
            </Row>
        )}

        {/* Componente ChangeLog */}
        <ChangeLog
          open={changeLogVisible}
          onClose={handleCloseChangeLog}
          data={changeLogData}
          logs={changeLogLogs}
        />
      </>
    </WithPermission>
  );
};

export default Settings;